/**
 * Central brand configuration. All UI, SMS, and legal copy must use these tokens.
 * Replace via env without changing workflows or template keys.
 */

export type BrandConfig = {
  businessName: string;
  fullBusinessName: string;
  legalEntityName: string;
  supportPhone: string;
  supportEmail: string;
  smsSenderName: string;
  siteUrl: string;
  privacyPolicyUrl: string;
  termsUrl: string;
  tagline: string;
  customerPromise: string;
  promisePoints: string[];
};

const DEFAULT_PROMISE_POINTS = [
  "Arrival notification before each visit",
  "Complete pet-waste removal",
  "Sanitized tools",
  "Secured-gate photo confirmation after every service",
];

/** Static `process.env.NEXT_PUBLIC_*` reads so Next can inline them on client + server. */
function pub(value: string | undefined, fallback: string): string {
  const v = value?.trim();
  return v || fallback;
}

export function getBrand(): BrandConfig {
  const businessName = pub(
    process.env.NEXT_PUBLIC_BUSINESS_NAME,
    "Scoop & Secure",
  );
  const fullBusinessName = pub(
    process.env.NEXT_PUBLIC_FULL_BUSINESS_NAME,
    "A + Scoop & Secure Pet Waste Removal",
  );

  return {
    businessName,
    fullBusinessName,
    legalEntityName: pub(
      process.env.NEXT_PUBLIC_LEGAL_ENTITY_NAME,
      `${fullBusinessName} LLC`,
    ),
    supportPhone: pub(process.env.NEXT_PUBLIC_SUPPORT_PHONE, "+16025129201"),
    supportEmail: pub(
      process.env.NEXT_PUBLIC_SUPPORT_EMAIL,
      "support@scoopandsecure.com",
    ),
    smsSenderName: pub(process.env.NEXT_PUBLIC_SMS_SENDER_NAME, businessName),
    siteUrl: pub(process.env.NEXT_PUBLIC_SITE_URL, "http://localhost:3001"),
    privacyPolicyUrl: "/privacy",
    termsUrl: "/terms",
    tagline: pub(
      process.env.NEXT_PUBLIC_TAGLINE,
      "Clean yard. Secured gate. Every visit.",
    ),
    customerPromise: pub(
      process.env.NEXT_PUBLIC_CUSTOMER_PROMISE,
      "Arrival notification, complete pet-waste removal, sanitized tools, and secured-gate photo confirmation after every service.",
    ),
    promisePoints: DEFAULT_PROMISE_POINTS,
  };
}

/** Live brand tokens — re-reads env on each access (safe for scripts after dotenv). */
export const brand: BrandConfig = new Proxy({} as BrandConfig, {
  get(_target, prop: string | symbol) {
    if (typeof prop !== "string") return undefined;
    const current = getBrand();
    return current[prop as keyof BrandConfig];
  },
});
